/**
 * Machine d'état pour les référents
 * Gère les transitions d'état valides et les règles métier associées
 * Conformément aux exigences 1.3, 3.1, 3.2, 3.3, 3.4
 */

import { ReferentStatus } from '../../types/states';
import { StateTransitionError, BusinessRuleViolationError } from '../../errors/BusinessErrors';

/**
 * Contexte pour les transitions d'état des référents
 */
export interface ReferentTransitionContext {
  referentId: string;
  campaignId: string;
  userId?: string;
  reason?: string;
  evaluationData?: {
    isComplete: boolean;
    completedSteps: string[];
    totalSteps: number;
    hasSignature?: boolean;
    hasVoiceRecording?: boolean;
  };
  reminderCount?: number;
  lastReminderDate?: Date;
  invitationDate?: Date;
}

/**
 * Résultat d'une validation de transition
 */
export interface ReferentTransitionValidationResult {
  isValid: boolean;
  reason?: string;
  requiredConditions?: string[];
}

/**
 * Machine d'état pour les référents
 * Définit les transitions autorisées et valide les règles métier
 */
export class ReferentStateMachine {
  /**
   * Définition des transitions autorisées entre états
   * Chaque état source peut transitionner vers les états listés
   */
  private static readonly transitions: Record<ReferentStatus, ReferentStatus[]> = {
    [ReferentStatus.INVITE]: [
      ReferentStatus.A_REPONDU,
      ReferentStatus.RELANCE
    ],
    [ReferentStatus.RELANCE]: [
      ReferentStatus.A_REPONDU,
      ReferentStatus.RELANCE // Permet plusieurs relances
    ],
    [ReferentStatus.A_REPONDU]: [
      ReferentStatus.COMPLETE,
      ReferentStatus.RELANCE // Peut être relancé pour compléter
    ],
    [ReferentStatus.COMPLETE]: []
  };

  /**
   * Conditions métier requises pour certaines transitions
   */
  private static readonly transitionConditions: Record<string, (context: ReferentTransitionContext) => ReferentTransitionValidationResult> = {
    [`${ReferentStatus.INVITE}->${ReferentStatus.A_REPONDU}`]: (context) => {
      // Le référent doit avoir commencé à répondre au formulaire
      if (!context.evaluationData) {
        return {
          isValid: false,
          reason: 'Aucune donnée d\'évaluation trouvée',
          requiredConditions: ['Le référent doit commencer à remplir le formulaire']
        };
      }
      return { isValid: true };
    },

    [`${ReferentStatus.INVITE}->${ReferentStatus.RELANCE}`]: (context) => {
      // Vérifier qu'un délai minimum s'est écoulé depuis l'invitation
      if (context.invitationDate) {
        const daysSinceInvitation = Math.floor(
          (Date.now() - context.invitationDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        
        if (daysSinceInvitation < 1) {
          return {
            isValid: false,
            reason: 'Un délai minimum de 24h doit s\'écouler avant d\'envoyer une relance',
            requiredConditions: ['Attendre au moins 24h après l\'invitation initiale']
          };
        }
      }
      return { isValid: true };
    },

    [`${ReferentStatus.RELANCE}->${ReferentStatus.A_REPONDU}`]: (context) => {
      // Le référent doit avoir commencé à répondre au formulaire
      if (!context.evaluationData) {
        return {
          isValid: false,
          reason: 'Aucune donnée d\'évaluation trouvée',
          requiredConditions: ['Le référent doit commencer à remplir le formulaire']
        };
      }
      return { isValid: true };
    },

    [`${ReferentStatus.RELANCE}->${ReferentStatus.RELANCE}`]: (context) => {
      // Vérifier qu'un délai minimum s'est écoulé depuis la dernière relance
      if (context.lastReminderDate) {
        const daysSinceLastReminder = Math.floor(
          (Date.now() - context.lastReminderDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        
        if (daysSinceLastReminder < 2) {
          return {
            isValid: false,
            reason: 'Un délai minimum de 48h doit s\'écouler entre les relances',
            requiredConditions: ['Attendre au moins 48h entre les relances']
          };
        }
      }

      // Limiter le nombre de relances
      const maxReminders = 3;
      if (context.reminderCount && context.reminderCount >= maxReminders) {
        return {
          isValid: false,
          reason: `Nombre maximum de relances atteint (${maxReminders})`,
          requiredConditions: [`Maximum ${maxReminders} relances autorisées`]
        };
      }

      return { isValid: true };
    },

    [`${ReferentStatus.A_REPONDU}->${ReferentStatus.COMPLETE}`]: (context) => {
      // Vérifier que l'évaluation est complète
      if (!context.evaluationData) {
        return {
          isValid: false,
          reason: 'Aucune donnée d\'évaluation trouvée',
          requiredConditions: ['Données d\'évaluation requises']
        };
      }

      if (!context.evaluationData.isComplete) {
        return {
          isValid: false,
          reason: 'L\'évaluation n\'est pas complète',
          requiredConditions: ['Toutes les étapes d\'évaluation doivent être terminées']
        };
      }

      // Vérifier les éléments obligatoires selon les exigences
      const missingRequirements: string[] = [];

      if (context.evaluationData.completedSteps.length < context.evaluationData.totalSteps) {
        missingRequirements.push('Toutes les étapes du formulaire doivent être complétées');
      }

      if (missingRequirements.length > 0) {
        return {
          isValid: false,
          reason: 'Éléments obligatoires manquants pour compléter l\'évaluation',
          requiredConditions: missingRequirements
        };
      }

      return { isValid: true };
    },

    [`${ReferentStatus.A_REPONDU}->${ReferentStatus.RELANCE}`]: (context) => {
      // Permet de relancer un référent qui a commencé mais n'a pas terminé
      if (context.evaluationData?.isComplete) {
        return {
          isValid: false,
          reason: 'Le référent a déjà terminé son évaluation',
          requiredConditions: ['L\'évaluation ne doit pas être complète pour envoyer une relance']
        };
      }

      // Vérifier le délai depuis la dernière activité
      if (context.lastReminderDate) {
        const daysSinceLastReminder = Math.floor(
          (Date.now() - context.lastReminderDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        
        if (daysSinceLastReminder < 2) {
          return {
            isValid: false,
            reason: 'Un délai minimum de 48h doit s\'écouler avant d\'envoyer une nouvelle relance',
            requiredConditions: ['Attendre au moins 48h depuis la dernière relance']
          };
        }
      }

      return { isValid: true };
    }
  };

  /**
   * Vérifie si une transition est autorisée au niveau de la machine d'état
   * @param fromState État source
   * @param toState État cible
   * @returns true si la transition est autorisée, false sinon
   */
  static canTransition(fromState: ReferentStatus, toState: ReferentStatus): boolean {
    const allowedTransitions = this.transitions[fromState];
    return allowedTransitions?.includes(toState) ?? false;
  }

  /**
   * Retourne la liste des états vers lesquels une transition est possible
   * @param currentState État actuel
   * @returns Liste des états de destination possibles
   */
  static getValidTransitions(currentState: ReferentStatus): ReferentStatus[] {
    return this.transitions[currentState] ?? [];
  }

  /**
   * Valide une transition avec les règles métier
   * @param fromState État source
   * @param toState État cible
   * @param context Contexte de la transition
   * @returns Résultat de la validation
   */
  static validateTransition(
    fromState: ReferentStatus,
    toState: ReferentStatus,
    context: ReferentTransitionContext
  ): ReferentTransitionValidationResult {
    // Vérification de base de la transition
    if (!this.canTransition(fromState, toState)) {
      return {
        isValid: false,
        reason: `Transition non autorisée de ${fromState} vers ${toState}`,
        requiredConditions: [`L'état ${fromState} ne peut pas transitionner vers ${toState}`]
      };
    }

    // Vérification des conditions métier spécifiques
    const transitionKey = `${fromState}->${toState}`;
    const conditionValidator = this.transitionConditions[transitionKey];

    if (conditionValidator) {
      return conditionValidator(context);
    }

    // Transition autorisée sans conditions spéciales
    return { isValid: true };
  }

  /**
   * Effectue une transition d'état avec validation complète
   * @param fromState État source
   * @param toState État cible
   * @param context Contexte de la transition
   * @throws StateTransitionError si la transition n'est pas autorisée
   * @throws BusinessRuleViolationError si les règles métier ne sont pas respectées
   */
  static executeTransition(
    fromState: ReferentStatus,
    toState: ReferentStatus,
    context: ReferentTransitionContext
  ): void {
    const validation = this.validateTransition(fromState, toState, context);

    if (!validation.isValid) {
      if (validation.reason?.includes('non autorisée')) {
        throw new StateTransitionError(
          fromState,
          toState,
          'referent',
          context
        );
      } else {
        throw new BusinessRuleViolationError(
          `Transition ${fromState} -> ${toState}`,
          validation.reason || 'Conditions métier non respectées',
          {
            ...context,
            requiredConditions: validation.requiredConditions
          }
        );
      }
    }
  }

  /**
   * Détermine automatiquement le prochain état basé sur le contexte
   * Utilisé pour la progression automatique des référents
   * @param currentState État actuel
   * @param context Contexte du référent
   * @returns Prochain état suggéré ou null si aucune progression automatique n'est possible
   */
  static getAutoProgressionState(
    currentState: ReferentStatus,
    context: ReferentTransitionContext
  ): ReferentStatus | null {
    switch (currentState) {
      case ReferentStatus.INVITE:
      case ReferentStatus.RELANCE:
        // Progression automatique vers A_REPONDU quand le référent commence à répondre
        if (context.evaluationData && context.evaluationData.completedSteps.length > 0) {
          return ReferentStatus.A_REPONDU;
        }
        break;

      case ReferentStatus.A_REPONDU:
        // Progression automatique vers COMPLETE quand l'évaluation est terminée
        if (context.evaluationData?.isComplete) {
          return ReferentStatus.COMPLETE;
        }
        break;

      default:
        // Pas de progression automatique pour les autres états
        break;
    }

    return null;
  }

  /**
   * Vérifie si un état est terminal (aucune transition possible)
   * @param state État à vérifier
   * @returns true si l'état est terminal
   */
  static isTerminalState(state: ReferentStatus): boolean {
    return this.getValidTransitions(state).length === 0;
  }

  /**
   * Vérifie si un état permet l'envoi de relances
   * @param state État à vérifier
   * @returns true si les relances sont autorisées
   */
  static allowsReminders(state: ReferentStatus): boolean {
    return [
      ReferentStatus.INVITE,
      ReferentStatus.RELANCE,
      ReferentStatus.A_REPONDU
    ].includes(state);
  }

  /**
   * Vérifie si un état permet la modification des réponses
   * @param state État à vérifier
   * @returns true si les modifications sont autorisées
   */
  static allowsModification(state: ReferentStatus): boolean {
    return [
      ReferentStatus.INVITE,
      ReferentStatus.RELANCE,
      ReferentStatus.A_REPONDU
    ].includes(state);
  }

  /**
   * Retourne une description lisible de l'état
   * @param state État à décrire
   * @returns Description de l'état
   */
  static getStateDescription(state: ReferentStatus): string {
    const descriptions: Record<ReferentStatus, string> = {
      [ReferentStatus.INVITE]: 'Le référent a été invité et n\'a pas encore commencé son évaluation',
      [ReferentStatus.RELANCE]: 'Le référent a été relancé pour compléter son évaluation',
      [ReferentStatus.A_REPONDU]: 'Le référent a commencé son évaluation mais ne l\'a pas encore terminée',
      [ReferentStatus.COMPLETE]: 'Le référent a terminé son évaluation complète'
    };

    return descriptions[state] || 'État inconnu';
  }

  /**
   * Retourne les actions possibles pour un état donné
   * @param state État actuel
   * @returns Liste des actions possibles
   */
  static getAvailableActions(state: ReferentStatus): string[] {
    const actions: Record<ReferentStatus, string[]> = {
      [ReferentStatus.INVITE]: [
        'Envoyer une relance',
        'Voir les détails de l\'invitation'
      ],
      [ReferentStatus.RELANCE]: [
        'Envoyer une nouvelle relance',
        'Voir l\'historique des relances'
      ],
      [ReferentStatus.A_REPONDU]: [
        'Voir le progrès de l\'évaluation',
        'Envoyer une relance pour compléter',
        'Voir les réponses partielles'
      ],
      [ReferentStatus.COMPLETE]: [
        'Voir l\'évaluation complète',
        'Télécharger les réponses',
        'Voir l\'historique'
      ]
    };

    return actions[state] || [];
  }

  /**
   * Calcule le pourcentage de progression de l'évaluation
   * @param context Contexte du référent
   * @returns Pourcentage de progression (0-100)
   */
  static getProgressPercentage(context: ReferentTransitionContext): number {
    if (!context.evaluationData) {
      return 0;
    }

    const { completedSteps, totalSteps } = context.evaluationData;
    
    if (totalSteps === 0) {
      return 0;
    }

    return Math.round((completedSteps.length / totalSteps) * 100);
  }

  /**
   * Détermine si un référent peut être relancé
   * @param state État actuel
   * @param context Contexte du référent
   * @returns true si une relance est possible
   */
  static canSendReminder(state: ReferentStatus, context: ReferentTransitionContext): boolean {
    if (!this.allowsReminders(state)) {
      return false;
    }

    // Vérifier les conditions de délai et de limite
    const validation = this.validateTransition(state, ReferentStatus.RELANCE, context);
    return validation.isValid;
  }

  /**
   * Retourne le nombre de jours depuis la dernière activité
   * @param context Contexte du référent
   * @returns Nombre de jours depuis la dernière activité
   */
  static getDaysSinceLastActivity(context: ReferentTransitionContext): number {
    const lastActivity = context.lastReminderDate || context.invitationDate;
    
    if (!lastActivity) {
      return 0;
    }

    return Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Retourne les étapes manquantes pour compléter l'évaluation
   * @param context Contexte du référent
   * @returns Liste des étapes manquantes
   */
  static getMissingSteps(context: ReferentTransitionContext): string[] {
    if (!context.evaluationData) {
      return ['Toutes les étapes'];
    }

    const { completedSteps, totalSteps } = context.evaluationData;
    const allSteps = Array.from({ length: totalSteps }, (_, i) => `Étape ${i + 1}`);
    
    return allSteps.filter((_, index) => !completedSteps.includes(`step_${index + 1}`));
  }
}